import React from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { GuidedRiskFinding, GuidedRiskCategory } from './arkoGuidedFlows';

const CATEGORY_STYLE: Record<
  GuidedRiskCategory,
  { label: string; border: string; bg: string; text: string; icon: React.ElementType }
> = {
  kritisch: {
    label: 'Kritisch',
    border: 'border-red-500/30',
    bg: 'bg-red-500/10',
    text: 'text-red-200',
    icon: AlertTriangle,
  },
  hinweis: {
    label: 'Hinweis',
    border: 'border-amber-500/30',
    bg: 'bg-amber-500/10',
    text: 'text-amber-100',
    icon: Info,
  },
  ok: {
    label: 'Unkritisch',
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-100',
    icon: CheckCircle2,
  },
};

export interface PortalGuidedRiskPanelProps {
  findings: GuidedRiskFinding[];
  className?: string;
}

export const PortalGuidedRiskPanel: React.FC<PortalGuidedRiskPanelProps> = ({
  findings,
  className = '',
}) => {
  if (!findings.length) return null;

  const grouped: Record<GuidedRiskCategory, GuidedRiskFinding[]> = {
    kritisch: [],
    hinweis: [],
    ok: [],
  };
  for (const f of findings) grouped[f.category].push(f);

  const order: GuidedRiskCategory[] = ['kritisch', 'hinweis', 'ok'];

  return (
    <div className={`space-y-2 max-w-[min(100%,32rem)] ${className}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 pl-0.5">
        Risiko-Übersicht
      </p>
      {order.map((cat) => {
        const items = grouped[cat];
        if (!items.length) return null;
        const style = CATEGORY_STYLE[cat];
        const Icon = style.icon;
        return (
          <div key={cat} className={`rounded-lg border ${style.border} ${style.bg} p-2.5`}>
            <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${style.text}`}>
              <Icon className="w-3.5 h-3.5" aria-hidden />
              {style.label} ({items.length})
            </div>
            <ul className="space-y-1.5">
              {items.map((item, i) => (
                <li key={`${cat}-${i}`} className="text-xs text-slate-200 leading-relaxed">
                  <span className="font-medium">{item.title}</span>
                  {item.detail && item.detail !== item.title ? (
                    <span className="text-slate-400"> — {item.detail}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};
